import { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import appConfig from 'configs/app.config';
import {
  Notification as NotificationA,
  toast,
  Button,
  Avatar,
  Alert
} from 'components/ui';
import io from 'socket.io-client';

const normalizarUuidSocket = valor =>
  String(valor || '')
    .trim()
    .toLowerCase();

const defaultSocketContext = {
  state: {
    onlineUsers: [],
    busyUsers: [],
    lastChatMessage: null,
    pendingChatOpen: null,
    connected: false
  },
  setState: () => null,
  emitSocket: () => null,
  registerWebRTCCallback: () => null
};

export const AppSocket = createContext(defaultSocketContext);
export const useCoreSocket = () => useContext(AppSocket) || defaultSocketContext;

const Socket = ({ children }) => {
  const [state, setState] = useState({
    onlineUsers: [],
    busyUsers: [],
    lastChatMessage: null,
    pendingChatOpen: null,
    connected: false
  });

  const socketRef = useRef(null);
  const webrtcCallbackRef = useRef(null);

  const emitSocket = useCallback((event, data) => {
    socketRef.current?.emit(event, data);
  }, []);

  const registerWebRTCCallback = useCallback((callback) => {
    webrtcCallbackRef.current = callback;
  }, []);

  useEffect(() => {
    if (localStorage.getItem('datos')) {
      const datos = JSON.parse(localStorage.getItem('datos'));
      const socket = io(`${appConfig.apiUrl}`);
      socketRef.current = socket;

      socket.on('connect', () => {
        setState(prev => ({ ...prev, connected: true }));
        socket.emit('subscribe', [
          normalizarUuidSocket(datos.usuario.sk_usuario),
          normalizarUuidSocket(datos.usuario.sk_grupo_empresarial)
        ]);
      });

      socket.on('disconnect', () => {
        setState(prev => ({ ...prev, connected: false }));
      });

      socket.on('DLOREAN_SOCKET', datos => {
        switch (datos.type) {
          case 'presence':
            setState(prev => ({
              ...prev,
              onlineUsers: Array.isArray(datos.onlineUsers)
                ? [...new Set(datos.onlineUsers)]
                : [],
              busyUsers: Array.isArray(datos.busyUsers)
                ? [...new Set(datos.busyUsers)]
                : []
            }));
            break;
          case 'chat':
            setState(prev => ({ ...prev, lastChatMessage: datos }));
            if (Notification.permission === 'granted') {
              navigator.serviceWorker.ready.then(registration => {
                registration.showNotification(datos.title || 'Nuevo mensaje', {
                  body: `${datos.message}`,
                  icon: '/logo_square.png'
                });
              });
            }
            const abrirChatDesdeNotificacion = () => {
              setState(prev => ({
                ...prev,
                pendingChatOpen: {
                  sk_usuario: datos?.remitente?.sk_usuario,
                  nombre: datos?.nombre_remitente || datos?.title || 'Usuario',
                  sk_chat_conversacion: datos?.sk_chat_conversacion,
                  s_tipo_conversacion: datos?.s_tipo_conversacion || 'direct',
                  s_nombre_conversacion: datos?.s_nombre_conversacion || null
                }
              }));
            };
            toast.push(
              <NotificationA
                title={datos.title || 'Nuevo mensaje'}
                closable
                type="avatar"
                duration={3000}
                customIcon={
                  <Avatar shape="circle" src="/img/avatars/thumb-1.jpg" />
                }
                onClick={(e) => {
                  // Ignorar clic en el botón cerrar (X)
                  if (e?.target?.closest?.('[role="button"]') || e?.target?.closest?.('.notification-close')) return;
                  abrirChatDesdeNotificacion();
                  toast.remove();
                }}
                className="cursor-pointer"
              >
                {datos.message}
              </NotificationA>,
              { placement: 'bottom-end' }
            );
            break;
          case 'notify':
            if (Notification.permission === 'granted') {
              navigator.serviceWorker.ready.then(registration => {
                registration.showNotification(datos.title, {
                  body: `${datos.message}`,
                  icon: '/logo_square.png'
                });
              });
            }
            toast.push(
              <NotificationA
                title="Notificación"
                closable
                type="info"
                duration={10000}
              >
                {datos.message}
              </NotificationA>,
              { placement: 'bottom-end' }
            );
            break;
          case 'webrtc-signal':
            if (webrtcCallbackRef.current) {
              webrtcCallbackRef.current(datos);
            }
            break;
          default:
            console.log(datos);
            break;
        }
      });

      return () => {
        socket.disconnect();
        socketRef.current = null;
      };
    }
  }, []);

  return (
    <AppSocket.Provider value={{ state, setState, emitSocket, registerWebRTCCallback }}>
      {children}
    </AppSocket.Provider>
  );
};

export default Socket;

